import { PatientEngagementDto } from './dto/patient-engagement.dto';
import { convertFilterToPaginateQueryParams } from 'src/utils/gridView';
import {
  PaginateQuery,
  paginateQueryToURLParams,
  PaginateQueryWithStats,
} from 'src/api/pagination/dtos';
import { PatientEngagementContactAttemptDto } from './dto/patient-engagment-contact-attempt.dto';
import { CreatePatientEngagementDto } from './dto/create-patient-engagement.dto';
import { apiClient } from 'src/careflow/ph-core/api_client/api_client';

export interface PatientEngagementResponse {
  createdBy: string;
  createdOn: string;
  updatedBy: string;
  updatedOn: string;
  patientEngagementId: string;
  patientEngagementUUID: string;
  careflowIdentifierPatientEngagement: string;
  fhirId: string;
  workflowInstanceId: number;
  workflowStageId: string;
  workflowStageName: string;
  workflowStageOrder: number;
  workflowStatus: string;
  workflowSubstatus: string;
  workflowDispositionReason: string;
  workflowDispositionNote: string;
  workflowDispositionOn: string;
  workflowDispositionAt: string;
  workflowOwnedBy: string;
  workflowScope: string;
  workflowReferenceName: string;
  patientFamily: string;
  patientGiven: string;
  patientName: string;
  patientFhirUUID: string;
  patientDOB: string;
  patientDOBFormatted: string;
  patientAge: string;
  patientEmail: string;
  patientPhone: string;
  patientMrn: string;
  patientLanguage: string;
  patientManagingOrgUUID: string;
  patientManagingOrgDisplay: string;
  patientLastSIL: string;
  contactAttemptContactOn: string;
}

export interface PatientEngagementContactAttemptResponse {
  patientEngagementId: string;
  fhirId: string;
  patientEngagementContactAttemtpId: string;
  patientEngagementContactAttemptUuid: string;
  patientEngagementContactAttemptOutcome: string;
  patientEngagementContactAttemptContactOn: string;
  patientEngagementContactAttemptNextContactOn: string;
  patientEngagementContactAttemptNoteId: string;
  patientEngagementContactAttemptDuration: string;
  noteId: string;
  noteUuid: string;
  noteType: string;
  noteText: string;
}

export interface PaginatedPatientEngagementResponse {
  data: PatientEngagementResponse[];
  links: {
    current: string;
  };
  meta: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface PaginatedPatientEngagementDto {
  data: PatientEngagementDto[];
  links: {
    current: string;
  };
  meta: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
  stats?: any;
}

const apiBase = 'engagement/patient';

const PatientApi = {
  getAll: async (
    queryParams: PaginateQuery
  ): Promise<PaginatedPatientEngagementResponse | undefined> => {
    const urlParams = paginateQueryToURLParams(queryParams);
    const filters = convertFilterToPaginateQueryParams(queryParams?.filter ?? {});
    const url = `${apiBase}/?${urlParams.toString()}${filters}`;

    return apiClient.get<PaginatedPatientEngagementResponse | undefined>(url);
  },

  getOne: async (id: string): Promise<PatientEngagementResponse | undefined> => {
    return apiClient.get<PatientEngagementResponse | undefined>(
      `${apiBase}/${id}`
    );
  },

  getAllContactAttempt: async (
    patientEngagementId: string
  ): Promise<PatientEngagementContactAttemptResponse[] | undefined> => {
    return apiClient.get<PatientEngagementContactAttemptResponse[] | undefined>(
      `${apiBase}/contact-attempt/${patientEngagementId}`
    );
  },

  createPatientEngagement: async (
    payload: CreatePatientEngagementDto
  ): Promise<PaginatedPatientEngagementResponse | undefined> => {
    return apiClient.post<CreatePatientEngagementDto,any | undefined>(
      `${apiBase}`, payload
    );
  },

  createContactAttempt: async (
    payload: PatientEngagementContactAttemptDto
  ): Promise<any | undefined> => {
    return apiClient.post<PatientEngagementContactAttemptDto,any | undefined>(
      `${apiBase}/contact-attempt`, payload
    );
  },

  bulkAssignOwner: async (payload: any): Promise<any | undefined> => {
    return apiClient.post<any, undefined>(
      `${apiBase}/bulkAssignOwner`,
      payload
    );
  },

  getStatistics: async (id: string): Promise<any | undefined> => {
    return apiClient.get<any | undefined>(
      `${apiBase}/statistics/${id}`
    );
  }
}

export const PatientEngagementService = {
  getAll: async (
    queryParams: PaginateQueryWithStats
  ): Promise<PaginatedPatientEngagementDto | undefined> => {
    const patientEngagements = await PatientApi.getAll(queryParams);
    if (!patientEngagements) return undefined;
    const dtos = patientEngagements.data.map((r) => {
      const dto = new PatientEngagementDto();
      dto.fromPatientEngagementResponse(r);
      return dto;
    });
    const mapped: PaginatedPatientEngagementDto = { ...patientEngagements, data: dtos };
    return mapped;
  },

  getOne: async (id: string) : Promise<PatientEngagementResponse | undefined> => {
    const patientEnagement = await PatientApi.getOne(id);
    if (!patientEnagement) return undefined;
    return patientEnagement;
  },

  getContactAttempt: async (
    fhirId: string
  ): Promise<PatientEngagementContactAttemptResponse[] | undefined> => {
    const contactAttempts = await PatientApi.getAllContactAttempt(fhirId);
    if (!contactAttempts) return undefined;
    return contactAttempts;
  },

  createPatientEngagement: async (
    payload: CreatePatientEngagementDto
  ): Promise<PatientEngagementResponse | undefined> => {
    const patientEngagement = await PatientApi.createPatientEngagement(payload);
    if (patientEngagement) return undefined;
    return patientEngagement;
  },

  createContactAttempt: async (
    payload: PatientEngagementContactAttemptDto
  ): Promise<any | undefined> => {
    const contactAttempt = await PatientApi.createContactAttempt(payload);
    if (!contactAttempt) return undefined;
    return contactAttempt;
  },

  bulkAssignOwner: async (payload: any): Promise<any | undefined> => {
    const bulkAssignOwner = await PatientApi.bulkAssignOwner(payload);
    if (!bulkAssignOwner) return undefined;
    return bulkAssignOwner;
  },

  getStatistics: async (id: string): Promise<any | undefined> => {
    const statistics = await PatientApi.getStatistics(id);
    if(!statistics) return undefined;
    return statistics;
  }
}
